import * as React from 'react'
import * as Yup from 'yup'
import { History } from 'history'
import { Field, FieldProps, Formik, FormikProps } from 'formik'
import { IAddOn, IMap, IProviderProductObject, IProviderServiceLevel } from '../../../../../../types'
import EditBottom from '../EditBottom'
import ImageUpload from '../../../../../ui/ImageUpload'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import FormLabel from 'src/components/ui/form/FormLabel'
import UIMessages from 'src/utils/ui-messages'
import { IFormValues } from 'src/components/assistance/AddAsistanceForm/AddAssistanceCreateForm'
import { Input, Select, Textarea } from '@mobiscroll/react-latest'
import { withNotification } from 'src/components/ui/notification-provider'
import history from 'src/history'

interface InnerFormValues {
  allottedTimeAtDropoff: number
  allottedTimeAtPickup: number
  id: string
  name: string
  internalName: string
  serviceLevelId: string
  description: string
  externalUrl: string
  estimatedLoadingTime: number
  bookingPriority: string
}

const InnerForm = (serviceLevels: IMap<IProviderServiceLevel>) => (props: FormikProps<InnerFormValues>) => {
  const {
    initialValues,
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    isSubmitting,
    setFieldValue
  } = props

  const priorityOptions = [{ text: 'Low', value: 'low' }, { text: 'High', value: 'high' }]

  const textInput = ({ field }: FieldProps<IFormValues>, label: string, type: string = 'text') => {
    if ((field.name === 'firstName' && field.value !== '') || (field.name === 'lastName' && field.value !== '')) {
      field.value = field.value.charAt(0).toUpperCase() + field.value.slice(1)
    }
    return (
      <Input
        type={type}
        className='mobiscroll-input form'
        {...field}
        label={label}
        theme='ios'
        themeVariant='light'
        inputStyle='box'
        labelStyle='floating'
      />
    )
  }

  const getDefaultServiceLevelValue = () => {
    const serviceLevelOptions = Object.values(serviceLevels).map(i => ({ text: i.name, value: i.id }))
    const serviceLevel = serviceLevelOptions.find(option => option.value === values.serviceLevelId)
    if (serviceLevel !== undefined) {
      return serviceLevel.text
    } else {
      return ''
    }
  }

  const setServiceLevel = (option: any) => {
    //Set Value
    setFieldValue('serviceLevelId', option.value)
  }

  const getDefaultBookingPriority = () => {
    const bookingPriority = priorityOptions.find(option => option.value === values.bookingPriority)
    if (bookingPriority !== undefined) {
      return bookingPriority.text
    } else {
      return ''
    }
  }

  const setBookingPriority = (option: any) => {
    //Set Value
    setFieldValue('bookingPriority', option.value)
  }

  return (
    <form onSubmit={handleSubmit}>
      <FloatingForm fullHeight={true}>
        <>
          <div className='form-modal-section full-section'>
            <div className='flex flex-row mb1'>
              <div style={{ flex: 1 }}>
                {/* Label from ui-messages */}
                <FormLabel>{UIMessages.common.photo}</FormLabel>
                {/* Logo get from import ImageUpload from '../../../ui/ImageUpload'  */}
                <ImageUpload name={values.name} entityId={values.id ? values.id : undefined} />
                {/* Logo get from import ImageUpload from '../../../ui/ImageUpload'  */}
              </div>
            </div>
            <div className='flex mt1 justify-between'>
              <div className='col-6 pr1'>
                <Field name='name' render={field => textInput(field, `Product Name`)} />
                <div className='error'>
                  {touched.name && errors.name && <span className='h6 font-red'>{errors.name}</span>}
                </div>
              </div>
              <div className='col-6'>
                <Select
                  name='serviceLevelId'
                  data={Object.values(serviceLevels).map(i => ({ text: i.name, value: i.id }))}
                  value={getDefaultServiceLevelValue()}
                  onChange={setServiceLevel}
                  placeholder='Select Service Level'
                  touchUi={false}
                  theme='ios'
                  themeVariant='light'
                  inputStyle='box'
                  label={`Service Level`}
                  labelStyle='floating'
                />
                <div className='error'>
                  {touched.serviceLevelId && errors.serviceLevelId && (
                    <span className='ErrorMsgDivForType font-small weight-300 font-red block relative h0'>
                      {errors.serviceLevelId}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className='mt1'>
              <Textarea
                name='description'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.description}
                label={'Product Description'}
                theme='ios'
                themeVariant='light'
                inputStyle='box'
                labelStyle='floating'
              />
            </div>
            <div className='flex mt1 justify-between'>
              <div className='col-6 pr1'>
                <Field name='externalUrl' render={field => textInput(field, `External URL`)} />
                <div className='error'>
                  {touched.externalUrl && errors.externalUrl && (
                    <span className='h6 font-red'>{errors.externalUrl}</span>
                  )}
                </div>
              </div>
              <div className='col-6'>
                <Field name='internalName' render={field => textInput(field, `Internal Name`)} />
                <div className='error'>
                  {touched.internalName && errors.internalName && (
                    <span className='h6 font-red'>{errors.internalName}</span>
                  )}
                </div>
              </div>
            </div>
            <div className='flex mt1 justify-between'>
              <div className='col-6 pr1'>
                <Field
                  name='allottedTimeAtPickup'
                  render={field => textInput(field, 'Allotted Time at Pickup(minutes)', 'number')}
                />
                <div className='error'>
                  {touched.allottedTimeAtPickup && errors.allottedTimeAtPickup && (
                    <span className='h6 font-red'>{errors.allottedTimeAtPickup}</span>
                  )}
                </div>
              </div>
              <div className='col-6'>
                <Field
                  name='allottedTimeAtDropoff'
                  render={field => textInput(field, 'Allotted Time at Drop-off(minutes)', 'number')}
                />
                <div className='error'>
                  {touched.allottedTimeAtDropoff && errors.allottedTimeAtDropoff && (
                    <span className='h6 font-red'>{errors.allottedTimeAtDropoff}</span>
                  )}
                </div>
              </div>
            </div>
            <div className='flex mt1 justify-between'>
              <div className='col-6 pr1'>
                <Field
                  name='estimatedLoadingTime'
                  render={field => textInput(field, `Allotted Loading Time`, `number`)}
                />
                <div className='error'>
                  {touched.estimatedLoadingTime && errors.estimatedLoadingTime && (
                    <span className='h6 font-red'>{errors.estimatedLoadingTime}</span>
                  )}
                </div>
              </div>
              <div className='col-6'>
                <Select
                  name='bookingPriority'
                  data={priorityOptions}
                  value={getDefaultBookingPriority()}
                  onChange={setBookingPriority}
                  placeholder='Select Booking Priority'
                  touchUi={false}
                  theme='ios'
                  themeVariant='light'
                  inputStyle='box'
                  label={`Booking Priority`}
                  labelStyle='floating'
                />
              </div>
            </div>
          </div>
        </>
      </FloatingForm>
    </form>
  )
}

interface IProductInfoFormProps {
  product: IProviderProductObject
  serviceLevels: IMap<IProviderServiceLevel>
  saveProduct: (product: IProviderProductObject) => Promise<any>
  history?: History
  notification?: any
}

interface IProductInfoFormState {
  isSaved: boolean
  isSaving: boolean
}

class ProductInfoForm extends React.Component<IProductInfoFormProps, IProductInfoFormState> {
  public validationSchema = Yup.object().shape({
    name: Yup.string().required('product name is required'),
    allottedTimeAtPickup: Yup.number()
      .min(0, 'Allotted time cannot be negative')
      .max(180, 'Allotted time cannot exceed 180 minutes'),
    allottedTimeAtDropoff: Yup.number()
      .min(0, 'Allotted time cannot be negative')
      .max(180, 'Allotted time cannot exceed 180 minutes')
    //externalUrl: Yup.string()
    //.test('url test', 'invalid external url, include http or https.', value => isValidURL(value))
    //.nullable(true)
  })

  private infoFormRef: any

  constructor(props: IProductInfoFormProps) {
    super(props)
    this.state = {
      isSaved: false,
      isSaving: false
    }
  }

  public render() {
    const { product, history } = this.props
    const { isSaved, isSaving } = this.state
    let { serviceLevelId } = product
    if (!serviceLevelId && product.providerServiceLevel) {
      serviceLevelId = product.providerServiceLevel.id
    }
    return (
      <div>
        <section>
          <Formik
            initialValues={{
              id: product ? product.id : '',
              name: product ? product.name : '',
              internalName: product ? product.internalName : '',
              description: product ? product.description : '',
              externalUrl: product ? product.externalUrl : null,
              serviceLevelId,
              estimatedLoadingTime: product ? product.estimatedLoadingTime : 0,
              bookingPriority: product ? product.bookingPriority : 'low',
              allottedTimeAtDropoff: product ? product.allottedTimeAtDropoff : 0,
              allottedTimeAtPickup: product ? product.allottedTimeAtPickup : 0
            }}
            ref={this.setInfoFormRef}
            validationSchema={this.validationSchema}
            onSubmit={this.onSave}
            render={InnerForm(this.props.serviceLevels)}
          />
        </section>
        <EditBottom
          isSaving={isSaving}
          cancelPath={`/products/${product.id}/info`}
          onSave={this.onSave}
          history={history}
        />
      </div>
    )
  }

  private setInfoFormRef = (node: any) => {
    this.infoFormRef = node
  }

  private onSave = () => {
    const { values, errors } = this.infoFormRef.state
    const { product, saveProduct, notification } = this.props
    if (Object.keys(errors).length > 0) {
      alert('Cannot update product. Please check validation errors')
      return
    } else {
      const updatedProduct = Object.assign({}, product, values)

      const fixedValues = {
        ...updatedProduct,
        pricing: {
          ...updatedProduct.pricing,
          addOn: updatedProduct.pricing.addOn.reduce((acc: Record<string, number>, item: IAddOn) => {
            acc[item.label] = item.price
            return acc
          }, {})
        }
      }

      saveProduct(fixedValues).then(response => {
        notification.addNotification({
          title: 'Success',
          message: 'Product Information Saved Successfully',
          level: 'success'
        })
      })
      history.push(`/products/${product.id}/info`)
    }
  }
}

export default withNotification(ProductInfoForm)



// WEBPACK FOOTER //
// ./src/components/products/details/product/edit/pane/ProductInfo.tsx